
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface SavedContact {
  id: string;
  name: string;
  phone: string;
  email?: string;
  label?: string;
}

export const useContactBook = () => {
  const [savedContacts, setSavedContacts] = useState<SavedContact[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSavedContacts = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data, error } = await supabase
          .from('contact_book')
          .select('id, name, phone, email, label')
          .eq('user_id', user.id)
          .eq('is_saved', true);
          
        if (error) {
          console.error("Error fetching saved contacts:", error);
          setSavedContacts([]);
        } else {
          setSavedContacts(data || []);
        }
      }
    } catch (error) {
      console.error("Error in useContactBook:", error);
      setSavedContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Add the missing saveContact function
  const saveContact = async (name: string, phone: string, email?: string, label?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication error",
          description: "You must be logged in to save contacts.",
          variant: "destructive"
        });
        return;
      }

      if (!name || !phone) {
        toast({
          title: "Validation error",
          description: "Contact name and phone are required.",
          variant: "destructive"
        });
        return;
      }

      const { data, error } = await supabase
        .from('contact_book')
        .insert({
          user_id: user.id,
          name,
          phone,
          email: email || null,
          label: label || null,
          is_saved: true
        })
        .select('id, name')
        .single();
        
      if (error) {
        console.error("Error saving contact:", error);
        toast({
          title: "Error",
          description: "Failed to save contact. Please try again.",
          variant: "destructive"
        });
        return;
      }
      
      toast({
        title: "Contact saved",
        description: `${data.name} has been added to your contacts.`
      });
      
      // Refresh the contacts list
      fetchSavedContacts();
      
      return data.id;
    } catch (error) {
      console.error("Error in saveContact:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchSavedContacts();
  }, []);

  return {
    savedContacts,
    isLoading,
    refetch: fetchSavedContacts,
    saveContact
  };
};
